import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  adjacentColumnTarget,
  type BoardDetail,
  type Card,
  type CardTarget,
  type ColumnTarget,
  hiddenColumnCount,
  kanbanColumns,
  moveCardLocally,
  moveColumnLocally,
  normalizeCardTarget,
  orderCalendarColumn,
  useCopyColumn,
  useCreateCard,
  useCreateColumn,
  useDeleteColumn,
  useSortColumn,
  useMoveCard,
  useMoveColumn,
  useSetCardArchived,
  useSetCardCompleted,
  useSetCardPinned,
  useUndoTrash,
  useUpdateColumn,
} from '@/entities/board';
import { useParticipants } from '@/entities/collab';
import { isNote, useNoteToCard } from '@/entities/note';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import { useErrorMessage } from '@/shared/i18n';
import { Button, useToast } from '@/shared/ui';

import { type ColumnActions, ColumnView } from './ColumnView';
import { cardTargetFromDrop, columnTargetFromDrop, isCard, isColumn } from './dnd';
import { InlineForm } from './InlineForm';
import { usePanScroll } from './usePanScroll';
import styles from './Kanban.module.css';

interface BoardCanvasProps {
  detail: BoardDetail;
  /** Ids of cards matching the active filter; `null` when nothing is filtered. */
  visibleCardIds?: ReadonlySet<string> | null;
  /**
   * A filter or search is active and its result is known: columns without matching cards are
   * hidden (a view-only rule: the board itself is untouched).
   */
  filterActive?: boolean;
  /** A filter is set but its result is not known yet: nothing is hidden, nothing can be dragged. */
  filterPending?: boolean;
  /** Clears every filter and the search (offered in the "columns hidden" notice). */
  onResetFilters?: () => void;
  onOpenCard: (cardId: string) => void;
}

/** The Kanban board: columns, cards, drag-and-drop and their button alternatives. */
const NO_CARDS: Card[] = [];

export function BoardCanvas({
  detail,
  visibleCardIds = null,
  filterActive = false,
  filterPending = false,
  onResetFilters,
  onOpenCard,
}: BoardCanvasProps) {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const boardId = detail.board.id;
  const [addingColumn, setAddingColumn] = useState(false);
  const canManageColumns = can(detail.permissions, PERMISSIONS.boardManageColumns);
  const participants = useParticipants(boardId);
  // Executors of each card as avatars; arrays stay identical until the data changes.
  const assigneesByCard = useMemo(() => {
    const names = new Map((participants.data ?? []).map((p) => [p.user_id, p.display_name]));
    const result = new Map<string, { id: string; name: string }[]>();
    for (const card of detail.cards) {
      if (card.assignee_ids.length === 0) continue;
      result.set(
        card.id,
        card.assignee_ids.map((id) => ({ id, name: names.get(id) ?? '…' })),
      );
    }
    return result;
  }, [detail.cards, participants.data]);
  const canvasRef = useRef<HTMLDivElement>(null);
  usePanScroll(canvasRef);

  const createColumn = useCreateColumn(boardId);
  const updateColumn = useUpdateColumn(boardId);
  const moveColumn = useMoveColumn(boardId);
  const deleteColumn = useDeleteColumn(boardId);
  const createCard = useCreateCard(boardId);
  const moveCard = useMoveCard(boardId);
  const setCompleted = useSetCardCompleted(boardId);
  const setArchived = useSetCardArchived(boardId);
  const setPinned = useSetCardPinned(boardId);
  const copyColumn = useCopyColumn(boardId);
  const sortColumn = useSortColumn(boardId);
  const noteToCard = useNoteToCard();
  // Notes being converted right now: a drop is handled once per note at a time.
  const converting = useRef(new Set<string>());
  const visibility = { visibleCardIds, filterActive };
  const shownColumns = kanbanColumns(detail, visibility);
  const hiddenColumns = hiddenColumnCount(detail, visibility);
  const toast = useToast();
  // Undo has its own mutations: their errors are shown in the toast, not in the board banner.
  const undoArchive = useSetCardArchived(boardId);
  const undoTrash = useUndoTrash(boardId);

  const requestCardMove = (cardId: string, rawTarget: CardTarget) => {
    // Within the Calendar column the order is chronological: a drop there is not a reorder.
    const calendarId = detail.columns.find((c) => c.system_kind === 'calendar')?.id;
    const from = detail.cards.find((c) => c.id === cardId)?.placement.column_id;
    if (calendarId !== undefined && from === calendarId && rawTarget.columnId === calendarId) {
      return;
    }
    const target = normalizeCardTarget(detail, cardId, rawTarget);
    if (moveCardLocally(detail, cardId, target)) moveCard.mutate({ cardId, target });
  };
  const requestColumnMove = (columnId: string, target: ColumnTarget) => {
    if (moveColumnLocally(detail, columnId, target)) moveColumn.mutate({ columnId, target });
  };

  // A note (Mysliarium) dropped on a column becomes a card made from a COPY of the note; the
  // note stays. One fresh request id per drop makes a repeated handling harmless on the server.
  const createCardFromNote = (noteId: string, columnId: string) => {
    if (converting.current.has(noteId)) return;
    converting.current.add(noteId);
    noteToCard.mutate(
      { noteId, columnId, requestId: crypto.randomUUID() },
      {
        onSuccess: (result) => {
          toast.show({
            message: t('mysliarium.taskCreated'),
            actionLabel: t('toast.open'),
            onAction: () => {
              onOpenCard(result.card.id);
              return Promise.resolve();
            },
          });
        },
        onError: (error) => {
          toast.show({ message: getErrorMessage(error) });
        },
        onSettled: () => {
          converting.current.delete(noteId);
        },
      },
    );
  };

  // Always sees the latest board state without re-subscribing the monitor.
  const onDrop = useEffectEvent(
    (source: Record<string | symbol, unknown>, target: Record<string | symbol, unknown>) => {
      if (isNote(source)) {
        const columnTarget = cardTargetFromDrop(target);
        if (columnTarget) createCardFromNote(source.noteId, columnTarget.columnId);
      } else if (isCard(source)) {
        const cardTarget = cardTargetFromDrop(target);
        if (cardTarget) requestCardMove(source.cardId, cardTarget);
      } else if (isColumn(source)) {
        const columnTarget = columnTargetFromDrop(target);
        if (columnTarget) requestColumnMove(source.columnId, columnTarget);
      }
    },
  );

  useEffect(
    () =>
      monitorForElements({
        onDrop: ({ source, location }) => {
          const target = location.current.dropTargets[0]?.data;
          if (target) onDrop(source.data, target);
        },
      }),
    [],
  );

  const baseActions: ColumnActions = {
    rename: (columnId, name) => updateColumn.mutateAsync({ columnId, name }),
    setDescription: (columnId, description) =>
      updateColumn.mutateAsync({ columnId, description: description.trim() || null }),
    move: (columnId, direction) => {
      const target = adjacentColumnTarget(detail, columnId, direction, shownColumns);
      if (target) requestColumnMove(columnId, target);
    },
    remove: (columnId) =>
      deleteColumn.mutateAsync(columnId).then(() => {
        toast.show({
          message: t('toast.columnTrashed'),
          actionLabel: t('toast.undo'),
          onAction: () => undoTrash.mutateAsync(columnId),
        });
      }),
    copy: (columnId) =>
      copyColumn.mutateAsync(columnId).then(() => {
        toast.show({ message: t('toast.columnCopied') });
      }),
    sort: (columnId, criterion) => {
      sortColumn.mutate(
        { columnId, criterion },
        {
          onSuccess: () => {
            toast.show({ message: t('toast.columnSorted') });
          },
        },
      );
    },
    addCard: (columnId, title) => createCard.mutateAsync({ columnId, title }),
    openCard: onOpenCard,
    toggleComplete: (cardId, completed, anchor) => {
      setCompleted.mutate({ cardId, completed, anchor });
    },
    archive: (cardId) => {
      setArchived.mutate(
        { cardId, archived: true },
        {
          onSuccess: () => {
            toast.show({
              message: t('toast.cardArchived'),
              actionLabel: t('toast.undo'),
              onAction: () => undoArchive.mutateAsync({ cardId, archived: false }),
            });
          },
        },
      );
    },
    unpin: (cardId) => {
      // One request at a time per card: a double click must not send unpin twice.
      if (setPinned.isPending && setPinned.variables.cardId === cardId) return;
      setPinned.mutate({ cardId, pinned: false });
    },
    isPinPending: (cardId) => setPinned.isPending && setPinned.variables.cardId === cardId,
  };

  // Tiles are memoized: give them handlers whose identity never changes (they always call the
  // latest closure), so a card is re-rendered only when its own data changes.
  const latestActions = useRef(baseActions);
  useLayoutEffect(() => {
    latestActions.current = baseActions;
  });
  const tileActions = useMemo(
    () => ({
      openCard: (cardId: string) => {
        latestActions.current.openCard(cardId);
      },
      toggleComplete: (cardId: string, completed: boolean, anchor?: Element) => {
        latestActions.current.toggleComplete(cardId, completed, anchor);
      },
      archive: (cardId: string) => {
        latestActions.current.archive(cardId);
      },
      unpin: (cardId: string) => {
        latestActions.current.unpin(cardId);
      },
    }),
    [],
  );
  const actions: ColumnActions = { ...baseActions, ...tileActions };
  // One pass over the cards (they arrive in display order) instead of a filter per column.
  const cardsByColumn = useMemo(() => {
    const groups = new Map<string, Card[]>();
    for (const card of detail.cards) {
      const group = groups.get(card.placement.column_id);
      if (group) group.push(card);
      else groups.set(card.placement.column_id, [card]);
    }
    // The system Calendar column is ordered by date, not by manual position.
    for (const column of detail.columns) {
      const group = column.system_kind === 'calendar' ? groups.get(column.id) : undefined;
      if (group) groups.set(column.id, orderCalendarColumn(group));
    }
    return groups;
  }, [detail.cards, detail.columns]);

  const moveError =
    moveCard.error ??
    moveColumn.error ??
    setCompleted.error ??
    setArchived.error ??
    setPinned.error ??
    deleteColumn.error ??
    copyColumn.error ??
    sortColumn.error;

  return (
    <div className={styles.canvasWrap} data-scale-scope="kanban">
      {moveError && (
        <p className={styles.boardError} role="alert">
          {getErrorMessage(moveError)}
        </p>
      )}
      {hiddenColumns > 0 && shownColumns.some((column) => column.system_kind === null) && (
        <p className={styles.hiddenColumns} aria-live="polite" data-testid="hidden-columns">
          {t('filter.columnsHidden', { count: hiddenColumns })}
          {onResetFilters && (
            <Button size="sm" variant="ghost" onClick={onResetFilters}>
              {t('filter.resetFilters')}
            </Button>
          )}
        </p>
      )}
      <div ref={canvasRef} className={styles.canvas}>
        {shownColumns.map((column, index) => {
          const columnCards = cardsByColumn.get(column.id) ?? NO_CARDS;
          return (
            <ColumnView
              key={column.id}
              column={column}
              board={detail}
              cards={
                visibleCardIds ? columnCards.filter((c) => visibleCardIds.has(c.id)) : columnCards
              }
              totalCards={columnCards.length}
              filtering={visibleCardIds !== null || filterPending}
              labels={detail.labels}
              isFirst={index === 0}
              isLast={index === shownColumns.length - 1}
              actions={actions}
              assigneesByCard={assigneesByCard}
            />
          );
        })}
        {filterActive && shownColumns.every((column) => column.system_kind !== null) && (
          <div className={styles.noMatches} role="status" data-testid="filter-no-results">
            <p>{t('filter.noResults')}</p>
            {onResetFilters && (
              <Button size="sm" onClick={onResetFilters}>
                {t('filter.resetFilters')}
              </Button>
            )}
          </div>
        )}
        {canManageColumns && (
          <div className={styles.addColumn}>
            {addingColumn ? (
              <InlineForm
                label={t('columns.name')}
                submitLabel={t('columns.add')}
                maxLength={100}
                keepOpen
                onSubmit={(name) => createColumn.mutateAsync({ name })}
                onCancel={() => {
                  setAddingColumn(false);
                }}
              />
            ) : (
              <Button
                // A new column would be empty, hence hidden by the very filter that is active.
                disabled={filterActive || filterPending}
                title={filterActive || filterPending ? t('filter.addColumnDisabled') : undefined}
                onClick={() => {
                  setAddingColumn(true);
                }}
              >
                + {t('columns.add')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
